import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Card,
  Checkbox,
  Col,
  Collapse,
  DatePicker,
  Descriptions,
  Input,
  Row,
  Select,
  Space,
  Steps,
  Table,
  Tag,
  Typography,
} from 'antd';
import { DownloadOutlined, FileExcelOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { api } from '../../api/client';
import {
  FILTER_LABELS,
  FULL_RANGE_PRESETS,
  formatBytes,
  formatWhen,
  fullReportRequest,
  isRunning,
  jobStep,
  sectionRows,
  type FullRangePreset,
  type FullReportCatalog,
  type FullReportJob,
  type FullReportRequest,
} from './fullReport';

const KIND_LABEL = { master: 'All records', transactional: 'By date range', config: 'Current settings' } as const;

function save(data: BlobPart, filename: string, type: string) {
  const url = URL.createObjectURL(new Blob([data], { type }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function apiMessage(err: unknown, fallback: string): string {
  const data = (err as { response?: { data?: { message?: string; fields?: Record<string, string> } } })?.response?.data;
  const fields = data?.fields ? Object.values(data.fields).join(' ') : '';
  return [data?.message, fields].filter(Boolean).join(' — ') || fallback;
}

/**
 * Reports → Full Data Report. A read-only snapshot of everything the panel
 * works with, generated on the server as one Excel workbook plus a CSV per
 * section. The page only starts the job, shows its progress and downloads the
 * files — it never loads the rows itself.
 */
export function FullDataReportPage() {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const [preset, setPreset] = useState<FullRangePreset>('allTime');
  const [custom, setCustom] = useState({ from: '', to: '' });
  const [picked, setPicked] = useState<string[] | null>(null);
  const [filters, setFilters] = useState<Record<string, string | undefined>>({});
  const [jobId, setJobId] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);

  const catalog = useQuery({
    queryKey: ['full-report', 'catalog'],
    queryFn: async () => (await api.get<FullReportCatalog>('/admin/reports/full/catalog')).data,
    staleTime: Infinity,
  });
  const recent = useQuery({
    queryKey: ['full-report', 'list'],
    queryFn: async () => (await api.get<{ items: FullReportJob[] }>('/admin/reports/full')).data.items,
  });
  // The report on screen: the one just started, else the newest one there is.
  const shownId = jobId ?? recent.data?.[0]?.id ?? null;
  const job = useQuery({
    queryKey: ['full-report', 'job', shownId],
    enabled: shownId !== null,
    queryFn: async () => (await api.get<FullReportJob>(`/admin/reports/full/${shownId}`)).data,
    refetchInterval: (q) => (isRunning(q.state.data) ? 2000 : false),
  });

  const all = useMemo(() => catalog.data?.sections.map((s) => s.key) ?? [], [catalog.data]);
  const sections = picked ?? all;
  const request = fullReportRequest(preset, new Date(), custom, sections, filters);

  const start = useMutation({
    mutationFn: async (body: FullReportRequest) => (await api.post<FullReportJob>('/admin/reports/full', body)).data,
    onSuccess: (created) => {
      setJobId(created.id);
      qc.setQueryData(['full-report', 'job', created.id], created);
      void qc.invalidateQueries({ queryKey: ['full-report', 'list'] });
    },
    onError: (err) => message.error(apiMessage(err, 'Could not start the report')),
  });

  async function download(target: FullReportJob, file: string) {
    setDownloading(file);
    try {
      const res = await api.get(`/admin/reports/full/${target.id}/download`, {
        params: { file },
        responseType: 'blob',
        // A large workbook takes longer than the client's default 15 s.
        timeout: 0,
      });
      const day = target.createdAt.slice(0, 10);
      if (file === 'xlsx') {
        save(res.data as BlobPart, `DUDE_FULL_DATA_REPORT_${day}.xlsx`, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
      } else {
        save(res.data as BlobPart, `dude_${file}_${day}.csv`, 'text/csv;charset=utf-8');
      }
    } catch (err) {
      message.error(apiMessage(err, 'Could not download the file. The report may have expired — generate it again.'));
    } finally {
      setDownloading(null);
    }
  }

  const current = job.data;
  const running = isRunning(current) || start.isPending;
  const filterOptions = catalog.data?.filters ?? {};

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <div>
        <Typography.Title level={3} style={{ color: 'white', margin: 0 }}>
          Full Data Report
        </Typography.Title>
        <Typography.Text type="secondary">
          Generate a complete read-only snapshot of data available through the Admin Panel. This is a report, not a
          database backup: nothing is changed, and passwords, tokens and keys are never included.{' '}
          <Link to="/reports">Back to Reports</Link>
        </Typography.Text>
      </div>

      {catalog.isError && <Alert type="error" showIcon message="Could not load the report sections. Reload the page." />}

      <Card title="Generate a report" loading={catalog.isLoading}>
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <Space wrap align="center">
            <Typography.Text strong>Date range</Typography.Text>
            <Select<FullRangePreset>
              aria-label="Date range"
              value={preset}
              onChange={setPreset}
              options={FULL_RANGE_PRESETS}
              style={{ minWidth: 180 }}
            />
            {preset === 'custom' && (
              <DatePicker.RangePicker
                onChange={(_dates, [from, to]) => setCustom({ from: from ?? '', to: to ?? '' })}
              />
            )}
            <Typography.Text type="secondary">
              Applies to the “By date range” sections. Users, listeners, wallets and settings always export all
              records.
            </Typography.Text>
          </Space>
          {request === null && <Alert type="info" showIcon message="Pick both dates of the custom range." />}

          <div>
            <Space style={{ marginBottom: 8 }}>
              <Typography.Text strong>Sections</Typography.Text>
              <Button size="small" onClick={() => setPicked(all)}>
                Select all
              </Button>
              <Button size="small" onClick={() => setPicked([])}>
                Clear
              </Button>
              <Typography.Text type="secondary">
                {sections.length} of {all.length} selected
              </Typography.Text>
            </Space>
            <Checkbox.Group value={sections} onChange={(v) => setPicked(v)} style={{ width: '100%' }}>
              <Row gutter={[12, 8]}>
                {catalog.data?.sections.map((s) => (
                  <Col xs={24} md={12} xl={8} key={s.key}>
                    <Checkbox value={s.key}>
                      <span>{s.title}</span> <Tag>{KIND_LABEL[s.kind]}</Tag>
                      <div>
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                          {s.description}
                        </Typography.Text>
                      </div>
                    </Checkbox>
                  </Col>
                ))}
              </Row>
            </Checkbox.Group>
          </div>

          <Collapse
            size="small"
            items={[
              {
                key: 'filters',
                label: `Filters (optional)${Object.values(filters).some((v) => v) ? ' — in use' : ''}`,
                children: (
                  <Space direction="vertical" size={8} style={{ width: '100%' }}>
                    <Typography.Text type="secondary">
                      A filter narrows only the sections that have that field. Leave everything empty for the complete
                      report. Paste a User ID or Listener ID from that person’s page.
                    </Typography.Text>
                    <Row gutter={[12, 12]}>
                      {Object.entries(FILTER_LABELS).map(([key, label]) => (
                        <Col xs={24} md={12} xl={8} key={key}>
                          <Typography.Text>{label}</Typography.Text>
                          {filterOptions[key] ? (
                            <Select
                              allowClear
                              aria-label={label}
                              placeholder="All"
                              style={{ width: '100%' }}
                              value={filters[key]}
                              onChange={(v: string | undefined) => setFilters((f) => ({ ...f, [key]: v }))}
                              options={filterOptions[key].map((v) => ({ value: v, label: v.replace(/_/g, ' ') }))}
                            />
                          ) : (
                            <Input
                              allowClear
                              aria-label={label}
                              placeholder={key.endsWith('Id') ? 'Paste the ID' : 'All'}
                              value={filters[key] ?? ''}
                              onChange={(e) => setFilters((f) => ({ ...f, [key]: e.target.value }))}
                            />
                          )}
                        </Col>
                      ))}
                    </Row>
                  </Space>
                ),
              },
            ]}
          />

          <Space wrap>
            <Button
              type="primary"
              size="large"
              icon={<FileExcelOutlined />}
              disabled={request === null || sections.length === 0 || running}
              loading={start.isPending}
              onClick={() => request && start.mutate(request)}
            >
              Generate Report
            </Button>
            {isRunning(current) && <Typography.Text type="secondary">A report is being generated…</Typography.Text>}
            {sections.length === 0 && <Typography.Text type="warning">Pick at least one section.</Typography.Text>}
          </Space>
        </Space>
      </Card>

      {current && (
        <Card
          title={current.status === 'ready' ? 'Report Ready' : current.status === 'failed' ? 'Report failed' : 'Generating report'}
          extra={<Tag>{current.range.label}</Tag>}
        >
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Steps
              size="small"
              current={jobStep(current).current}
              status={jobStep(current).status}
              items={[
                { title: 'Preparing' },
                {
                  title: 'Processing',
                  description:
                    current.status === 'processing'
                      ? `${current.progress.current ?? ''} (${current.progress.done} of ${current.progress.total} sections)`
                      : undefined,
                },
                { title: 'Ready' },
              ]}
            />
            {current.status === 'failed' && <Alert type="error" showIcon message={current.error ?? 'The report failed.'} />}
            {current.warnings.map((w) => (
              <Alert key={w} type="warning" showIcon message={w} />
            ))}

            <Descriptions size="small" column={{ xs: 1, md: 2, xl: 3 }} bordered>
              <Descriptions.Item label="Report ID">
                <Typography.Text copyable code>
                  {current.id}
                </Typography.Text>
              </Descriptions.Item>
              <Descriptions.Item label="Generated">{formatWhen(current.finishedAt ?? current.createdAt)}</Descriptions.Item>
              <Descriptions.Item label="Requested by">{current.requestedBy.name}</Descriptions.Item>
              <Descriptions.Item label="Date range">
                {current.range.from ? `${formatWhen(current.range.from)} – ${formatWhen(current.range.to)}` : 'All time'}
              </Descriptions.Item>
              <Descriptions.Item label="Total records exported">
                {current.totalRecords.toLocaleString('en-IN')}
              </Descriptions.Item>
              <Descriptions.Item label="Excel file size">{formatBytes(current.files.xlsxBytes)}</Descriptions.Item>
              <Descriptions.Item label="Filters">
                {Object.keys(current.filters).length
                  ? Object.entries(current.filters)
                      .map(([k, v]) => `${FILTER_LABELS[k] ?? k}: ${v}`)
                      .join(', ')
                  : 'None'}
              </Descriptions.Item>
              <Descriptions.Item label="Available until">{formatWhen(current.expiresAt)}</Descriptions.Item>
            </Descriptions>

            {current.status === 'ready' && (
              <Space wrap>
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  loading={downloading === 'xlsx'}
                  onClick={() => void download(current, 'xlsx')}
                >
                  Download Excel
                </Button>
                <Button loading={downloading === 'summary'} onClick={() => void download(current, 'summary')}>
                  Download summary CSV
                </Button>
                <Typography.Text type="secondary">Individual CSV files are in the table below.</Typography.Text>
              </Space>
            )}

            <Table
              size="small"
              rowKey="key"
              pagination={false}
              dataSource={sectionRows(current, catalog.data)}
              columns={[
                { title: 'Section', dataIndex: 'title' },
                {
                  title: 'Records',
                  dataIndex: 'records',
                  align: 'right',
                  render: (v: number) => v.toLocaleString('en-IN'),
                },
                {
                  title: 'CSV',
                  render: (_: unknown, row) =>
                    current.status !== 'ready' ? (
                      '—'
                    ) : row.csvBytes === null ? (
                      <Typography.Text type="secondary">No data</Typography.Text>
                    ) : (
                      <Button
                        size="small"
                        icon={<DownloadOutlined />}
                        loading={downloading === row.key}
                        aria-label={`Download ${row.title} CSV`}
                        onClick={() => void download(current, row.key)}
                      >
                        CSV ({formatBytes(row.csvBytes)})
                      </Button>
                    ),
                },
              ]}
              summary={() => (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0}>
                    <strong>Total records exported</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1} align="right">
                    <strong>{current.totalRecords.toLocaleString('en-IN')}</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={2} />
                </Table.Summary.Row>
              )}
            />
          </Space>
        </Card>
      )}

      {(recent.data?.length ?? 0) > 1 && (
        <Card title="Recent reports" size="small">
          <Table
            size="small"
            rowKey="id"
            pagination={false}
            dataSource={recent.data}
            columns={[
              { title: 'Generated', render: (_: unknown, r: FullReportJob) => formatWhen(r.createdAt) },
              { title: 'Requested by', render: (_: unknown, r: FullReportJob) => r.requestedBy.name },
              { title: 'Date range', render: (_: unknown, r: FullReportJob) => r.range.label },
              { title: 'Sections', render: (_: unknown, r: FullReportJob) => r.sections.length },
              {
                title: 'Records',
                align: 'right',
                render: (_: unknown, r: FullReportJob) => r.totalRecords.toLocaleString('en-IN'),
              },
              { title: 'Status', render: (_: unknown, r: FullReportJob) => <Tag>{r.status}</Tag> },
              {
                title: '',
                render: (_: unknown, r: FullReportJob) => (
                  <Button size="small" type="link" disabled={r.id === shownId} onClick={() => setJobId(r.id)}>
                    {r.id === shownId ? 'Shown above' : 'Open'}
                  </Button>
                ),
              },
            ]}
          />
          <Typography.Text type="secondary">
            Reports are deleted automatically {catalog.data?.expiresAfterHours ?? 24} hours after they are generated.
          </Typography.Text>
        </Card>
      )}
    </Space>
  );
}
